import React from "react";
import FilterTextInput from "../FilterTextInput";
import {
  Add,
  Clock,
  Edit2,
  ExportSquare,
  TickCircle,
  ArrowDown2,
  ArrowUp2,
  Trash,
} from "iconsax-react";
import Button from "../Button";
import { useNavigate } from "react-router-dom";
import { GUEST_ADD, GUEST_BULK_EDIT, GUEST_EDIT } from "../../Router/path";
import axios from "axios";
import { EXPORT_GUEST_API, EXPORT_HOTEL_SUBMISSION_API } from "../../Constant/api";
import { useToast } from "../Toast";
import fileDownload from "js-file-download";
import { motion as m } from "framer-motion";
import DropdownSet from "../DropdownSet";
import { XMarkIcon } from "@heroicons/react/24/solid";
import Paginator from "../Paginator";
import Can from "../Can";

const GuestListTable = ({
  withHeaderAction,
  title,
  editable,
  searchable,
  data = [],
  meta,
  handlePageClick,
  searchTerm,
  handleSearch,
  handleKeyDown,
  handlePerPageChange,
  perPage,
  clearFilter,
  sortBy,
  sortOrder,
  handleSortOrderChange,
  tagOptions = [],
  tableOptions = [],
  statusOptions = [],
  handleDropdownChange,
  selectedTags,
  selectedTables,
  selectedStatus,
}) => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const exportGuestList = async () => {
    try {
      axios
        .post(
          EXPORT_GUEST_API,
          { eventId: sessionStorage.getItem("event_id") },
          {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
              TimezoneOffset: new Date().getTimezoneOffset(),
            },
            responseType: "blob",
          }
        )
        .then((res) => {
          fileDownload(res.data, "guest_list.xlsx");
        })
        .catch((err) => {
          toast("Failed to fetch guest list template.", "error");
          console.log(err);
        });
    } catch (error) {
      toast("Failed to fetch guest list template.", "error");
      console.log(error);
    }
  };

  const exportHotelSubmission = async () => {
    toast("Exporting hotel submission...", "info");
    axios
      .post(
        EXPORT_HOTEL_SUBMISSION_API,
        { eventId: sessionStorage.getItem("event_id") },
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
          },
          responseType: "blob",
        }
      )
      .then((res) => {
        const suggestedFileName = res.headers["x-suggested-filename"];
        const contentDisposition =
          res.headers["content-disposition"] ||
          res.headers["Content-Disposition"];
        let fileName = suggestedFileName || "hotel_submission.xlsx";

        if (!suggestedFileName && contentDisposition) {
          const fileNameMatch = contentDisposition.match(
            /filename\*?=['"]?(?:UTF-8'')?([^;'"\n]*)['"]?/i
          );
          if (fileNameMatch && fileNameMatch[1]) {
            fileName = decodeURIComponent(fileNameMatch[1]);
          }
        }
        fileDownload(res.data, fileName);
      })
      .catch((err) => {
        toast("Failed to export hotel submission.", "error");
        console.log(err);
      });
  };

  return (
    <m.div
      className="flex flex-col border border-[#EFF0F2] w-full h-fit bg-white rounded-xl"
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Title */}
      {title && (
        <div className="flex w-full border-b border-[#EFF0F2] px-[24px] py-[16px] justify-between items-center">
          <h2>{title}</h2>
        </div>
      )}

      {/* Header Action */}
      {withHeaderAction && (
        <div className="flex flex-col md:flex-row gap-4 w-full border-b border-[#EFF0F2] px-[24px] py-[16px] justify-between items-center">
          <h2>Guest List</h2>
          <div className="flex flex-col md:flex-row gap-[8px]">
            <Can permission="export_guest_list">
              <Button variant="secondary" onClick={exportGuestList}>
                <ExportSquare size="16" color="#000000" variant="Outline" />{" "}
                Export Guest List
              </Button>
            </Can>
            <Can permission="export_hotel_submission">
              <Button variant="secondary" onClick={exportHotelSubmission}>
                <ExportSquare size="16" color="#000000" variant="Outline" />{" "}
                Hotel Submission Export
              </Button>
            </Can>
            <Can permission="create_guest">
              <Button variant="secondary" onClick={() => navigate(GUEST_BULK_EDIT)}>
                Bulk Edit
              </Button>
              <Button variant="primary" onClick={() => navigate(GUEST_ADD)}>
                <Add size={16} color="#FFFFFF" variant="Outline" /> Add Guest
              </Button>
            </Can>
          </div>
        </div>
      )}

      {/* Group by & Filters */}
      {searchable && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 w-full border-b border-[#EFF0F2] px-[24px] py-[12px] gap-[8px] items-center">
          {tagOptions && (
            <DropdownSet
              options={tagOptions}
              label="Tags"
              name="tags"
              value={selectedTags}
              hiddenLabel
              isMulti
              showMultiAsString
              onChange={(name, value) => {
                handleDropdownChange(value, name);
              }}
            />
          )}
          {tableOptions && (
            <DropdownSet
              options={tableOptions}
              label="Table"
              name="tables"
              value={selectedTables}
              hiddenLabel
              isMulti
              showMultiAsString
              placeholder="Select Tables"
              onChange={(name, value) => {
                handleDropdownChange(value, name);
              }}
            />
          )}
          {statusOptions && (
            <DropdownSet
              options={statusOptions}
              label="Status"
              name="status"
              value={selectedStatus}
              hiddenLabel
              isMulti
              showMultiAsString
              onChange={(name, value) => {
                handleDropdownChange(value, name);
              }}
            />
          )}
          <FilterTextInput
            value={searchTerm}
            className="w-full"
            onChange={handleSearch}
            onKeyDown={handleKeyDown}
          />
        </div>
      )}

      {clearFilter &&
        (selectedTables?.length > 0 || selectedTags?.length > 0 || selectedStatus?.length > 0 || searchTerm !== "") && (
          <div className="flex items-center w-full px-[24px] py-[12px] gap-2">
            {selectedTags.length > 0 &&
              selectedTags.map((tag) => (
                <span
                  key={tag}
                  className="flex items-center bg-[#EFF0F2] px-[12px] py-[6px] rounded-full gap-2 text-[12px]"
                >
                  {tagOptions.find((option) => option.id === tag)?.name || tag}
                  <XMarkIcon
                    className="size-[12px] cursor-pointer"
                    onClick={() => {
                      const newTags = selectedTags.filter((t) => t !== tag);
                      handleDropdownChange(newTags, "tags");
                    }}
                  />
                </span>
              ))}
            {selectedTables?.length > 0 &&
              selectedTables.map((table) => (
                <span
                  key={table}
                  className="flex items-center bg-[#EFF0F2] px-[12px] py-[6px] rounded-full gap-2 text-[12px]"
                >
                  {tableOptions.find((option) => option.id === table)?.name || table}
                  <XMarkIcon
                    className="size-[12px] cursor-pointer"
                    onClick={() => {
                      const newTables = selectedTables.filter((t) => t !== table);
                      handleDropdownChange(newTables, "tables");
                    }}
                  />
                </span>
              ))}
            {selectedStatus?.length > 0 &&
              selectedStatus.map((status) => (
                <span
                  key={status}
                  className="flex items-center bg-[#EFF0F2] px-[12px] py-[6px] rounded-full gap-2 text-[12px]"
                >
                  {statusOptions.find((option) => option.id === status)?.name || (status === "1" ? "Checked" : "Pending")}
                  <XMarkIcon
                    className="size-[12px] cursor-pointer"
                    onClick={() => {
                      const newStatuses = selectedStatus.filter((s) => s !== status);
                      handleDropdownChange(newStatuses, "status");
                    }}
                  />
                </span>
              ))}
            <span
              className="flex items-center text-[#FF3030] hover:cursor-pointer hover:text-[#5C4BF5] gap-2"
              onClick={clearFilter}
            >
              <Trash size="16" variant="Bold" />
              Clear All
            </span>
          </div>
        )}

      {/* Table data */}
      <div className="flex w-full h-full gap-[24px] overflow-x-auto">
        {!data || data.length === 0 ? (
          <div className="flex w-full h-[290px] items-center border-[#EFF0F2] justify-center text-gray-500 text-[25px]">
            No guests found
          </div>
        ) : (
          <div className="w-full overflow-x-auto">
            <table className="w-full min-w-max table-auto">
              {/* Table Header */}
              <thead>
                <tr className="w-full bg-[#F7F8F9] border-b border-[#EFF0F2]">
                  <th
                    onClick={() => handleSortOrderChange("id")}
                    className="cursor-pointer text-start px-[24px] py-[16px] text-nowrap"
                  >
                    <div className="flex items-center gap-[8px]">
                      ID
                      {sortBy === "id" &&
                        (sortOrder === "asc" ? (
                          <ArrowDown2 size={14} />
                        ) : (
                          <ArrowUp2 size={14} />
                        ))}
                    </div>
                  </th>
                  <th className="text-start px-[24px] py-[16px] text-nowrap">
                    Title
                  </th>
                  <th
                    onClick={() => handleSortOrderChange("first_name")}
                    className="cursor-pointer text-start px-[24px] py-[16px] text-nowrap"
                  >
                    <div className="flex items-center gap-[8px]">
                      Guest Name
                      {sortBy === "first_name" &&
                        (sortOrder === "asc" ? (
                          <ArrowDown2 size={14} />
                        ) : (
                          <ArrowUp2 size={14} />
                        ))}
                    </div>
                  </th>
                  <th className="text-start px-[24px] py-[16px] text-nowrap">
                    Tags
                  </th>
                  <th className="text-start px-[24px] py-[16px] text-nowrap">
                    Check-In Status
                  </th>
                  <th className="text-start px-[24px] py-[16px] text-nowrap">
                    Email
                  </th>
                  <th className="text-start px-[24px] py-[16px] text-nowrap">
                    Contact
                  </th>
                  <th className="text-start px-[24px] py-[16px] text-nowrap">
                    Group
                  </th>
                  <th className="text-start px-[24px] py-[16px] text-nowrap">
                    Table
                  </th>
                  <th className="text-start px-[24px] py-[16px] text-nowrap">
                    Seat Number
                  </th>
                  <th className="text-start px-[24px] py-[16px] text-nowrap">
                    Special Dietary
                  </th>
                  {editable && (
                    <th className="text-start px-[24px] py-[16px] text-nowrap"></th>
                  )}
                </tr>
              </thead>

              {/* Table Body */}
              <tbody>
                {data &&
                  data.map((item, index) => (
                    <tr
                      key={item.id}
                      className="w-full font-normal border-b last:border-none border-[#EFF0F2]"
                    >
                      <td className="px-[24px] py-[16px]">
                        {item.id ?? "-"}
                      </td>
                      <td className="px-[24px] py-[16px]">
                        {item.salutation ?? "-"}
                      </td>
                      <td className="px-[24px] py-[16px]">
                        {item.name ?? "-"}
                      </td>
                      <td className="px-[24px] py-[16px]">
                        {item.tags && item.tags.length > 0 ? (
                          <div className="flex flex-wrap gap-x-[4px] gap-y-[4px] max-w-[220px]">
                            {item.tags.map((tag, idx) => (
                              <span
                                key={idx}
                                className="inline-block rounded-md px-[8px] py-[4px] text-[11px] font-medium"
                                style={{
                                  backgroundColor: tag.color + "30",
                                  color: tag.color,
                                  // backgroundColor: "#EFEDFE",
                                  // color: "#5C4BF5",
                                }}
                              >
                                {tag.name}
                              </span>
                            ))}
                          </div>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="px-[24px] py-[16px]">
                        {item.status === true ? (
                          <div className="flex w-fit rounded-md px-[8px] gap-[4px] items-center bg-[#F4FCF7] border border-[#E9F9EF] text-[#22C55E]">
                            <TickCircle size="16" variant="Bold" />
                            Checked
                          </div>
                        ) : (
                          <div className="flex w-fit rounded-md px-[8px] gap-[4px] items-center bg-[#FFFBF3] border border-[#FEF6E6] text-[#F0A009]">
                            <Clock size="16" variant="Bold" />
                            Pending
                          </div>
                        )}
                      </td>
                      <td className="px-[24px] py-[16px]">
                        {item.email ?? "-"}
                      </td>
                      <td className="px-[24px] py-[16px]">
                        {item.contact ?? "-"}
                      </td>
                      <td className="px-[24px] py-[16px]">
                        {item.group_name ?? "-"}
                      </td>
                      <td className="px-[24px] py-[16px]">
                        {item.table_name ?? "-"}
                      </td>
                      <td className="px-[24px] py-[16px]">
                        {item.position !== null && item.position !== undefined
                          ? item.position + 1
                          : "-"}
                      </td>
                      <td className="px-[24px] py-[16px] max-w-[200px]">
                        {item.dietary_name ?? "-"}
                      </td>
                      {editable && (
                        <td className="px-[24px] py-[16px]">
                          <Can permission="edit_guest">
                            <span
                              className="flex text-[#5C4BF5] text-[14px] items-center hover:underline cursor-pointer"
                              onClick={() =>
                                navigate(GUEST_EDIT, { state: item.id })
                              }
                            >
                              <Edit2 size="15" variant="Bold" />
                              Edit
                            </span>
                          </Can>
                        </td>
                      )}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {meta && (
        <Paginator
          meta={meta}
          handlePageClick={handlePageClick}
          perPage={perPage}
          handlePerPageChange={handlePerPageChange}
        />
      )}
    </m.div>
  );
};

export default GuestListTable;
